import React from 'react';
import { CloseIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Card,
  Collapse,
  Flex,
  FormLabel,
  Icon,
  IconButton,
  Input,
  SimpleGrid,
  Switch,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import CommonInput from 'components/commonInput/CommonInput';
import CommonSubmitBtn from 'components/commonInput/CommonSubmitBtn';
import CommonSwitchInput from 'components/commonInput/CommonSwitchInput';
import PageLoader from 'components/wholePageLoader/PageLoader';
import SyncDataLoader from 'components/wholePageLoader/SyncDataLoader';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { sideBarRoutes, weekDays } from 'utils/StaticData';
import { useColorModeVariables } from '../../../components/colorModeVarialbels/ColorModeValues';
import {
  AddMerchantData,
  EditMerchantData,
  ViewMerchantData,
} from '../../../reactRedux/thunks/merchnatThunk/merchantThunk';
import { merchantAddEditValidation } from '../../../utils/CommonValidation';
import GenerateLocationConfirmModal from '../../../components/popUpModel/GenerateLocationConfirmModal';
import GoogleMapAddress from './GoogleMapAddress';
import OperatingHours from './OperatingHours';
import StationAllocationListModal from './StationAllocationListModal';
import { MdAddLocationAlt } from 'react-icons/md';
import { VscQuestion } from 'react-icons/vsc';
import { timezoneOptions } from 'utils/StaticData';
import useRegions from 'hooks/useRegions';
import CommonReactSelect from 'components/reactSelectCommon/CommonReactSelect';
import { FaPlus } from 'react-icons/fa';
import requestApi from 'utils/Request';
import { PRICE_STRATEGY_REGION_LIST } from 'utils/StaticApiEndpoints';
import { City, Country, State } from 'country-state-city';
import { formatPostalCode } from 'utils/PostalCode';

const MerchantAddEdit = () => {
  const { editmerchantId } = useParams();
  const { regions } = useRegions();
  // const [validateForm, setValidateForm] = useState(false);
  const operatingHoursRef = useRef();
  const holidayHoursRef = useRef();
  const [stationIds, setStationIds] = useState([]);
  const [, setTempLocationId] = useState('0');
  const [, setPage] = useState(localStorage.getItem('currentPage') || 1);
  const [temStationIds, setTempStationIds] = useState([]);
  const [weeklySchedule, setWeeklySchedule] = useState(
    Array(7)
      .fill()
      .map((_, index) => ({
        dayNumber: index,
        dayName: [weekDays][index],
        isOpen: true,
        periods: [{ openingTime: '09:00', closingTime: '18:00' }],
      })),
  );
  const [temporaryClosures, setTemporaryClosures] = useState([]);

  const handleAddTemporaryClosure = () => {
    setTemporaryClosures([
      ...temporaryClosures,
      {
        date: '',
        isOpen: true,
        periods: [{ openingTime: '09:00', closingTime: '17:00' }],
        reason: '',
      },
    ]);
  };

  const handleRemoveTemporaryClosure = (index) => {
    const newClosures = temporaryClosures.filter((_, i) => i !== index);
    setTemporaryClosures(newClosures);
  };

  const handleTemporaryClosureChange = (index, field, value) => {
    const newClosures = [...temporaryClosures];
    const closure = newClosures[index];

    if (field === 'isOpen') {
      closure.isOpen = value;
      if (value && (!closure.periods || closure.periods.length === 0)) {
        // If toggling on and there are no periods, add a default one
        closure.periods = [{ openingTime: '09:00', closingTime: '17:00' }];
      } else if (!value) {
        // Optional: clear periods when toggling off, if desired
        // closure.periods = [];
      }
    } else {
      closure[field] = value;
    }

    setTemporaryClosures(newClosures);
  };

  const handleClosureTimeChange = (closureIndex, periodIndex, field, value) => {
    const newClosures = [...temporaryClosures];
    newClosures[closureIndex].periods[periodIndex][field] = value;
    setTemporaryClosures(newClosures);
  };

  const handleAddClosurePeriod = (closureIndex) => {
    const newClosures = [...temporaryClosures];
    if (newClosures[closureIndex].periods.length < 3) {
      // Limit to 3 periods
      newClosures[closureIndex].periods.push({
        openingTime: '',
        closingTime: '',
      });
      setTemporaryClosures(newClosures);
    }
  };

  const handleRemoveClosurePeriod = (closureIndex, periodIndex) => {
    const newClosures = [...temporaryClosures];
    if (newClosures[closureIndex].periods.length > 1) {
      newClosures[closureIndex].periods.splice(periodIndex, 1);
      setTemporaryClosures(newClosures);
    }
  };
  const formatTimeToHHMMSS = (timeString) => {
    if (!timeString) return '00:00:00';
    const [hours, minutes] = timeString.split(':');
    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:00`;
  };
  const [coordinates, setCoordinates] = useState({
    lat: 23.01052,
    lng: 72.51129,
  });
  const [generateLocationModal, setGenerateLocationModal] = useState(false);
  const [, setInitialAddress] = useState('');
  const [address, setAddress] = useState('');
  const [addressComponents, setAddressComponents] = useState(null);
  const [priceStrategyOptions, setPriceStrategyOptions] = useState([]);
  const [isPriceStrategyLoading, setIsPriceStrategyLoading] = useState(false);
  const [isDeductionExpanded, setIsDeductionExpanded] = useState(true);
  // ISO codes backing the cascading legal address dropdowns (country -> province -> city)
  const [legalCountryIso, setLegalCountryIso] = useState('');
  const [legalProvinceIso, setLegalProvinceIso] = useState('');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { textColor, brandStars } = useColorModeVariables();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { adminDetails } = useSelector((state) => state.authSlice);
  const { isLoading, viewMerchantDetails, isViewLoading, isGenerateLoading } =
    useSelector((state) => state.MerchantSlice);
  // Helper function to get current region ID
  const getCurrentRegionId = (watchedRegionId) => {
    // For edit mode, use viewMerchantDetails
    if (editmerchantId && viewMerchantDetails?.regionId?.regionId) {
      return viewMerchantDetails.regionId.regionId;
    }

    // For add mode with admin user, use selected dropdown value
    if (adminDetails?.role === sideBarRoutes?.Admin && watchedRegionId?.id) {
      return watchedRegionId.id;
    }

    // For non-admin users, use their region from adminDetails
    if (adminDetails?.region?.regionId) {
      return adminDetails?.region?.regionId;
    }

    return null;
  };

  // Helper function to get default deduction options based on region
  const getDefaultDeductionOptions = (watchedRegionId) => {
    const regionId = getCurrentRegionId(watchedRegionId);
    // Spain (id: 2): purchased powerbanks and transaction costs
    if (regionId === 2) {
      return {
        includePowerbankCost: false,
        includeCustomerPurchases: true,
        includeTransactionCost: true,
        includeEInvoiceFee: false,
      };
    }
    // Netherlands (id: 1): loss, purchased powerbanks, and transaction costs
    if (regionId === 1) {
      return {
        includePowerbankCost: true,
        includeCustomerPurchases: true,
        includeTransactionCost: true,
        includeEInvoiceFee: false,
      };
    }
    // United Kingdom (id: 4): all deductions except the E-invoice fee
    if (regionId === 4) {
      return {
        includePowerbankCost: true,
        includeCustomerPurchases: true,
        includeTransactionCost: true,
        includeEInvoiceFee: false,
      };
    }
    // Default for other regions (e.g. Luxembourg) or no region:
    // all deductions except the E-invoice fee
    return {
      includePowerbankCost: true,
      includeCustomerPurchases: true,
      includeTransactionCost: true,
      includeEInvoiceFee: false,
    };
  };

  // Helper function to get default timezone based on region
  const getDefaultTimezone = (regionId) => {
    if (!regionId) return null;
    const mapping = regions.find((item) => item.id === regionId);
    return mapping?.timezone || null;
  };

  // Helper function to get timezone object for react-select
  const getTimezoneOption = (timezoneId) => {
    return timezoneOptions.find((tz) => tz.id === timezoneId);
  };

  // Default values for the form. Deduction options default to ON so a merchant
  // created before any region is selected still has all deductions enabled.
  const defaultValues = {
    weeklySchedule: weeklySchedule,
    address: '',
    includePowerbankCost: true,
    includeCustomerPurchases: true,
    includeTransactionCost: true,
    includeEInvoiceFee: false,
  };

  const {
    handleSubmit,
    setValue,
    register,
    watch,
    formState: { errors },
    control,
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(merchantAddEditValidation),
    context: { role: adminDetails?.role },
    defaultValues,
  });

  // Cascading legal address options sourced from the static country-state-city dataset
  const countryOptions = useMemo(() => Country.getAllCountries(), []);
  const provinceOptions = useMemo(
    () => (legalCountryIso ? State.getStatesOfCountry(legalCountryIso) : []),
    [legalCountryIso],
  );
  const cityOptions = useMemo(
    () =>
      legalCountryIso && legalProvinceIso
        ? City.getCitiesOfState(legalCountryIso, legalProvinceIso)
        : [],
    [legalCountryIso, legalProvinceIso],
  );

  const formSchedule = watch('weeklySchedule');
  const watchedRegionId = watch('regionId');
  const selectedRegionId = useMemo(
    () => getCurrentRegionId(watchedRegionId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      watchedRegionId?.id,
      editmerchantId,
      viewMerchantDetails?.regionId?.regionId,
      adminDetails?.region?.regionId,
    ],
  );

  // Update deduction options and timezone when region changes in add mode
  useEffect(() => {
    if (!editmerchantId && watchedRegionId?.id) {
      const deductionDefaults = getDefaultDeductionOptions(watchedRegionId);
      setValue('includePowerbankCost', deductionDefaults.includePowerbankCost);
      setValue(
        'includeCustomerPurchases',
        deductionDefaults.includeCustomerPurchases,
      );
      setValue(
        'includeTransactionCost',
        deductionDefaults.includeTransactionCost,
      );
      setValue('includeEInvoiceFee', deductionDefaults.includeEInvoiceFee);

      // Auto-select timezone based on region
      const defaultTimezone = getDefaultTimezone(watchedRegionId?.id);
      if (defaultTimezone) {
        const timezoneOption = getTimezoneOption(defaultTimezone);
        setValue('timezone', timezoneOption);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedRegionId?.id, editmerchantId, setValue]);

  const handleSwitchChange = (index, checked) => {
    // This function is for the weekly schedule, which has a different signature
    // from the temporary closure's switch handler.
    const newSchedule = [...formSchedule];
    newSchedule[index] = {
      ...newSchedule[index],
      isOpen: checked,
      periods: checked
        ? newSchedule[index].periods?.length > 0
          ? newSchedule[index].periods
          : [{ openingTime: '09:00', closingTime: '18:00' }]
        : [],
    };
    setValue('weeklySchedule', newSchedule);
  };

  const handleWeeklySwitchChange = (index, field, value) => {
    // Adapt the call from OperatingHours to what the weekly schedule handler expects
    if (field === 'isOpen') {
      handleSwitchChange(index, value);
    }
  };

  const handleTimeChange = (index, periodIndex, field, value) => {
    const newSchedule = [...formSchedule];
    const newPeriods = [...newSchedule[index].periods];
    newPeriods[periodIndex] = {
      ...newPeriods[periodIndex],
      [field]: value,
    };
    newSchedule[index] = {
      ...newSchedule[index],
      periods: newPeriods,
    };
    setValue('weeklySchedule', newSchedule);
  };

  const handleAddPeriod = (index) => {
    const newSchedule = [...formSchedule];
    const newPeriods = [...newSchedule[index].periods];
    if (newPeriods.length < 3) {
      newPeriods.push({ openingTime: '09:00', closingTime: '18:00' });
    }
    newSchedule[index] = {
      ...newSchedule[index],
      periods: newPeriods,
    };
    setValue('weeklySchedule', newSchedule);
  };

  const handleRemovePeriod = (index, periodIndex) => {
    const newSchedule = [...formSchedule];
    const newPeriods = [...newSchedule[index].periods];
    if (newPeriods.length > 1) {
      newPeriods.splice(periodIndex, 1);
    }
    newSchedule[index] = {
      ...newSchedule[index],
      periods: newPeriods,
    };
    setValue('weeklySchedule', newSchedule);
  };

  const fetchPriceStrategiesByRegion = async (regionId) => {
    if (!regionId) {
      setPriceStrategyOptions([]);
      setValue('priceStrategyId', null);
      return;
    }

    try {
      setIsPriceStrategyLoading(true);
      const { data } = await requestApi.get(PRICE_STRATEGY_REGION_LIST, {
        params: { regionId },
      });
      const list = data?.[0]?.priceStrategies?.map((item) => {
        return {
          id: item?.id,
          name: item?.name,
          freeDuration: item?.freeDuration,
          daysLimit: item?.daysLimit,
          deposit: item?.deposit,
          overtimeFees: item?.overtimeFees,
          price: item?.price,
          priceMinute: item?.priceMinute,
          maxPrice: item?.maxPrice,
          baijePriceId: item?.baijePriceId,
        };
      });
      setPriceStrategyOptions(Array.isArray(list) ? list : []);
    } catch (_error) {
      setPriceStrategyOptions([]);
    } finally {
      setIsPriceStrategyLoading(false);
    }
  };

  const fetchAddress = (coordinates) => {
    if (!window.google?.maps?.Geocoder) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode(
      { location: { lat: coordinates[1], lng: coordinates[0] } },
      (results, status) => {
        if (status === 'OK' && results?.[0]) {
          setInitialAddress(results[0].formatted_address);
        }
      },
    );
  };

  const onSubmit = async (data) => {
    // Trigger validation in child component
    // Trigger validation in child components
    const operatingHoursError = await operatingHoursRef.current?.validate();
    const holidayHoursError = await holidayHoursRef.current?.validate();

    if (operatingHoursError || holidayHoursError) {
      return; // Prevent form submission
    }

    // Destructure and clean data
    const { priceStrategyId } = data;
    const cleanedData = { ...data };
    delete cleanedData.price;
    delete cleanedData.priceMinute;
    delete cleanedData.freeDuration;
    delete cleanedData.deposit;
    delete cleanedData.overtimeFees;
    delete cleanedData.maxPrice;
    delete cleanedData.daysLimit;
    delete cleanedData.weeklySchedule;
    delete cleanedData.priceStrategyId;

    const unifiedPayload = {
      // Common fields from original data
      ...(adminDetails?.role === sideBarRoutes?.Admin &&
        !editmerchantId && {
          regionId: data?.regionId?.id,
        }),
      commission: data?.commission ?? 0,
      bankAccountNumber: data?.bankAccountNumber,
      merchantEmail: data?.merchantEmail,
      legalAddressLine1: data?.legalAddressLine1,
      legalAddressLine2: data?.legalAddressLine2,
      legalZipCode: formatPostalCode(data?.legalZipCode, data?.legalCountry),
      legalCity: data?.legalCity,
      legalProvince: data?.legalProvince,
      legalCountry: data?.legalCountry,
      legalName: data?.legalName,
      NIF: data?.NIF,
      vatNumber: data?.vatNumber,
      name: data?.name,
      includePowerbankCost: data?.includePowerbankCost,
      includeCustomerPurchases: data?.includeCustomerPurchases,
      includeTransactionCost: data?.includeTransactionCost,
      includeEInvoiceFee: data?.includeEInvoiceFee,
      freeEvents: data?.freeEvents,
      locationId: data?.locationId,
      priceStrategyId: priceStrategyId?.id ?? priceStrategyId ?? null,
      address: address,
      ...(!editmerchantId && {
        addressComponents: addressComponents ?? undefined,
      }),
      coordinates: [coordinates.lng, coordinates.lat],

      // Processed schedule data
      merchantHours: formSchedule?.flatMap(
        (day) =>
          day.periods?.map((period) => ({
            dayOfWeek: day.dayNumber,
            isOpen: true,
            openTime: formatTimeToHHMMSS(period.openingTime),
            closeTime: formatTimeToHHMMSS(period.closingTime),
          })) || [],
      ),
      merchantTemporaryHours: temporaryClosures.map((closure) => ({
        date: closure.date,
        isClosed: !closure.isOpen,
        openTime: closure.isOpen
          ? closure.periods.map((p) => formatTimeToHHMMSS(p.openingTime))
          : [],
        closeTime: closure.isOpen
          ? closure.periods.map((p) => formatTimeToHHMMSS(p.closingTime))
          : [],
        remark: closure.reason,
      })),
      freeUsageMode: data?.freeUsageMode,
      timezone: data?.timezone?.id,
      stationIds: stationIds,
    };

    if (editmerchantId) {
      dispatch(
        EditMerchantData(editmerchantId, true, unifiedPayload, () => {
          navigate('/admin/merchants');
          setPage(localStorage.getItem('currentPage') || 1);
        }),
      );
    } else {
      setPage(localStorage.setItem('currentPage', 1) || 1);
      dispatch(
        AddMerchantData(
          {
            ...cleanedData,
            ...unifiedPayload,
          },
          () => {
            navigate('/admin/merchants');
          },
        ),
      );
    }
  };

  useEffect(() => {
    if (editmerchantId) {
      dispatch(ViewMerchantData({ id: editmerchantId }));
    }
  }, [dispatch, editmerchantId]);

  useEffect(() => {
    fetchPriceStrategiesByRegion(selectedRegionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegionId]);

  useEffect(() => {
    if (viewMerchantDetails && editmerchantId) {
      setValue('daysLimit', viewMerchantDetails?.daysLimit);
      setValue('deposit', viewMerchantDetails?.deposit);
      setValue('freeDuration', viewMerchantDetails?.freeDuration);
      setValue('name', viewMerchantDetails?.name);
      setValue('locationId', viewMerchantDetails?.locationId);
      setTempLocationId(viewMerchantDetails?.locationId);
      setValue('overtimeFees', viewMerchantDetails?.overtimeFees); // COMMENTED OUT
      setValue('priceMinute', viewMerchantDetails?.priceMinute);
      setValue('price', viewMerchantDetails?.price);
      setValue('maxPrice', viewMerchantDetails?.maxPrice);
      setValue('commission', viewMerchantDetails?.commission);
      setValue('bankAccountNumber', viewMerchantDetails?.bankAccountNumber);
      setValue('legalName', viewMerchantDetails?.legalName);
      setValue('NIF', viewMerchantDetails?.NIF);
      setValue('vatNumber', viewMerchantDetails?.vatNumber);
      setValue(
        'includePowerbankCost',
        viewMerchantDetails?.includePowerbankCost,
      );
      setValue(
        'includeCustomerPurchases',
        viewMerchantDetails?.includeCustomerPurchases,
      );
      setValue(
        'includeTransactionCost',
        viewMerchantDetails?.includeTransactionCost,
      );
      setValue('includeEInvoiceFee', viewMerchantDetails?.includeEInvoiceFee);
      setValue('freeUsageMode', viewMerchantDetails?.freeUsageMode);
      setValue('legalAddressLine1', viewMerchantDetails?.legalAddressLine1);
      setValue('legalAddressLine2', viewMerchantDetails?.legalAddressLine2);
      setValue('legalZipCode', viewMerchantDetails?.legalZipCode);
      setValue('legalCity', viewMerchantDetails?.legalCity);
      setValue('legalProvince', viewMerchantDetails?.legalProvince);
      setValue('legalCountry', viewMerchantDetails?.legalCountry);

      // Resolve ISO codes from stored names so the cascading dropdowns can hydrate
      const savedCountry = Country.getAllCountries().find(
        (country) => country.name === viewMerchantDetails?.legalCountry,
      );
      setLegalCountryIso(savedCountry?.isoCode || '');
      if (savedCountry?.isoCode) {
        const savedProvince = State.getStatesOfCountry(
          savedCountry.isoCode,
        ).find(
          (province) => province.name === viewMerchantDetails?.legalProvince,
        );
        setLegalProvinceIso(savedProvince?.isoCode || '');
      } else {
        setLegalProvinceIso('');
      }

      // Set temporary closures data if it exists
      if (viewMerchantDetails.merchantTemporaryHours?.length > 0) {
        const formattedClosures =
          viewMerchantDetails.merchantTemporaryHours.map((closure) => {
            // Convert date from ISO string to YYYY-MM-DD format
            let dateStr = '';
            if (closure.date) {
              try {
                const date = new Date(closure.date);
                dateStr = date.toISOString().split('T')[0];
              } catch (e) {
                dateStr = closure.date.split('T')[0] || '';
              }
            }

            // Convert isClosed to isOpen (opposite)
            const isOpen = !closure.isClosed;

            // Convert openTime and closeTime arrays to periods array
            let periods = [];
            if (
              closure.openTime &&
              closure.closeTime &&
              Array.isArray(closure.openTime) &&
              Array.isArray(closure.closeTime)
            ) {
              const maxLength = Math.max(
                closure.openTime.length,
                closure.closeTime.length,
              );
              periods = Array.from({ length: maxLength }, (_, index) => {
                const openTime = closure.openTime[index] || '';
                const closeTime = closure.closeTime[index] || '';
                // Convert "09:00:00" format to "09:00" format
                const formatTime = (timeStr) => {
                  if (!timeStr) return '';
                  return timeStr.length >= 5
                    ? timeStr.substring(0, 5)
                    : timeStr;
                };
                return {
                  openingTime: formatTime(openTime),
                  closingTime: formatTime(closeTime),
                };
              });
            } else if (isOpen) {
              // If open but no time arrays, add default period
              periods = [{ openingTime: '09:00', closingTime: '17:00' }];
            }

            return {
              date: dateStr,
              isOpen: isOpen,
              periods: periods,
              reason: closure.remark || '',
            };
          });
        setTemporaryClosures(formattedClosures);
      }
      setValue('merchantEmail', viewMerchantDetails?.merchantEmail);
      const stationIds =
        viewMerchantDetails?.stations?.map((station) => station.stationId) ||
        [];
      setStationIds(stationIds);
      setTempStationIds(stationIds);
      setAddress(viewMerchantDetails?.address);
      setValue('address', viewMerchantDetails?.address);
      if (
        viewMerchantDetails.coordinates &&
        viewMerchantDetails.coordinates.length === 2
      ) {
        setCoordinates({
          lat: viewMerchantDetails.coordinates[1],
          lng: viewMerchantDetails.coordinates[0],
        });
        fetchAddress(viewMerchantDetails.coordinates);
      }

      /////// set region value for admin user ///
      if (adminDetails?.role === sideBarRoutes?.Admin) {
        setValue(
          'regionId',
          regions?.find(
            (region) => region.id === viewMerchantDetails?.regionId?.regionId,
          ),
        );
      }

      // Set timezone value if it exists
      if (viewMerchantDetails?.timezone) {
        const timezoneOption = getTimezoneOption(viewMerchantDetails.timezone);
        if (timezoneOption) {
          setValue('timezone', timezoneOption);
        }
      }

      //// Set weekly schedule
      if (viewMerchantDetails.merchantHours) {
        const periodsMap = new Map();
        viewMerchantDetails.merchantHours.forEach((hour) => {
          if (!periodsMap.has(hour.dayOfWeek)) {
            periodsMap.set(hour.dayOfWeek, []);
          }
          periodsMap.get(hour.dayOfWeek).push({
            openingTime: hour.openTime.substring(0, 5),
            closingTime: hour.closeTime.substring(0, 5),
          });
        });

        const fullWeekSchedule = Array(7)
          .fill()
          .map((_, index) => {
            const periods = periodsMap.get(index) || [];
            return {
              dayNumber: index,
              dayName: weekDays[index],
              isOpen: periods.length > 0,
              periods: periods.length > 0 ? periods : [],
            };
          });

        setWeeklySchedule(fullWeekSchedule);
        setValue('weeklySchedule', fullWeekSchedule);
      }

      if (viewMerchantDetails?.priceStrategyId && priceStrategyOptions.length) {
        const selectedStrategy = priceStrategyOptions.find(
          (item) =>
            Number(item?.id) === Number(viewMerchantDetails.priceStrategyId),
        );
        if (selectedStrategy) {
          setValue('priceStrategyId', selectedStrategy);
        }
      }
    }
  }, [
    viewMerchantDetails,
    editmerchantId,
    setValue,
    priceStrategyOptions,
    regions,
    adminDetails,
  ]);

  const handleRemoveStationId = (stationIdToRemove) => {
    const updatedStationIds = stationIds.filter(
      (stationId) => stationId !== stationIdToRemove,
    );
    setStationIds(updatedStationIds);
    setTempStationIds(updatedStationIds);
  };

  const handleAddressChange = (newAddress, components) => {
    setAddress(newAddress);
    setValue('address', newAddress);
    if (components) setAddressComponents(components);
  };

  return (
    <>
      {isGenerateLoading && <SyncDataLoader />}
      <Box pt={{ base: '60px', md: '80px', xl: '80px' }}>
        {editmerchantId && (
          <Flex justifyContent="flex-end" mb={4} gap={4}>
            {viewMerchantDetails?.freeUsageMode && (
              <Text
                bg="#fbb03f"
                color="yellow.800"
                px={4}
                py={2}
                borderRadius="md"
                fontWeight="bold"
                fontSize="sm"
                boxShadow="sm"
              >
                Free Events
              </Text>
            )}

            {viewMerchantDetails?.inMaintenance && (
              <Text
                bg="red.200"
                color="red.600"
                px={4}
                py={2}
                borderRadius="md"
                fontWeight="bold"
                fontSize="sm"
                boxShadow="sm"
              >
                Merchant is in maintenance
              </Text>
            )}

            {viewMerchantDetails?.isTemporarilyClosed && (
              <Text
                bg="orange.200"
                color="orange.600"
                px={4}
                py={2}
                borderRadius="md"
                fontWeight="bold"
                fontSize="sm"
                boxShadow="sm"
              >
                Merchant is temporarily closed
              </Text>
            )}

            {viewMerchantDetails?.isComingSoon && (
              <Text
                bg="green.200"
                color="green.700"
                px={4}
                py={2}
                borderRadius="md"
                fontWeight="bold"
                fontSize="sm"
                boxShadow="sm"
              >
                Coming Soon
              </Text>
            )}
          </Flex>
        )}
        <Flex direction="column" align="center" justify="center">
          <Card
            w="100%"
            boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
            borderRadius="20px"
            paddingInline="40px"
            paddingBlock="50px"
            overflowX={{ sm: 'scroll', lg: 'hidden' }}
          >
            {isViewLoading ? (
              <PageLoader />
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                {/* General Information Section */}
                <Box mb={8}>
                  <Flex
                    justify="space-between"
                    align="center"
                    mb={6}
                    pb={2}
                    borderBottom="2px solid"
                    borderColor="primary"
                  >
                    <Text fontSize="xl" fontWeight="bold" color="gray.800">
                      GENERAL INFORMATION
                    </Text>
                  </Flex>
                  <SimpleGrid
                    columns={{ base: 1, md: 2, lg: 3 }}
                    spacing="24px"
                  >
                    {/* region dropdown for admin user */}
                    {adminDetails?.role === sideBarRoutes?.Admin && (
                      <Controller
                        name="regionId"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <CommonReactSelect
                            label="Region"
                            name="regionId"
                            type="select"
                            options={regions}
                            value={value}
                            onChange={onChange}
                            register={register}
                            isRequired={true}
                            error={errors?.regionId}
                            getOptionLabel={(e) => e?.label}
                            getOptionValue={(e) => e?.id}
                            isDisabled={!!editmerchantId}
                          />
                        )}
                      />
                    )}

                    {/* Timezone dropdown */}
                    <Controller
                      name="timezone"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <CommonReactSelect
                          label="Timezone"
                          name="timezone"
                          type="select"
                          options={timezoneOptions}
                          value={value}
                          onChange={onChange}
                          register={register}
                          isRequired={true}
                          error={errors?.timezone}
                          getOptionLabel={(e) => e?.label}
                          getOptionValue={(e) => e?.id}
                          isDisabled={!!editmerchantId}
                        />
                      )}
                    />

                    {/* Merchant Name */}
                    <CommonInput
                      type="text"
                      name="name"
                      labelText="Merchant Name"
                      textColor={textColor}
                      brandStars={brandStars}
                      placeholder="Merchant Name"
                      register={register('name')}
                      errors={errors?.name}
                    />

                    {/* location Id */}
                    {editmerchantId &&
                      (adminDetails?.role === sideBarRoutes?.Admin ||
                        adminDetails?.role === sideBarRoutes?.Dealer ||
                        adminDetails?.role === sideBarRoutes?.Seller) && (
                        <Button
                          type="button"
                          marginTop="30px"
                          bg={'primary'}
                          color={'white'}
                          _hover={{ bg: 'primary', opacity: 0.8 }}
                          height={'46px'}
                          gap={2}
                          onClick={() => {
                            setGenerateLocationModal(true);
                          }}
                        >
                          <MdAddLocationAlt size={20} />
                          Generate New Location ID
                        </Button>
                      )}

                    {editmerchantId && (
                      <CommonInput
                        type="text"
                        name="locationId"
                        labelText="Location Id"
                        textColor={textColor}
                        brandStars={brandStars}
                        placeholder="Location Id"
                        register={register('locationId')}
                        errors={errors?.locationId}
                        readOnly={
                          !(
                            adminDetails?.role === sideBarRoutes?.Admin &&
                            !!editmerchantId
                          )
                        }
                        isEmailReadOnly={
                          !(
                            adminDetails?.role === sideBarRoutes?.Admin &&
                            !!editmerchantId
                          )
                        }
                      />
                    )}

                    {/* Email */}
                    <CommonInput
                      type="text"
                      name="merchantEmail"
                      labelText="Email"
                      textColor={textColor}
                      brandStars={brandStars}
                      placeholder="email1@example.com, email2@example.com"
                      register={register('merchantEmail')}
                      errors={errors?.merchantEmail}
                      isValidate={false}
                    />

                    <GoogleMapAddress
                      coordinates={coordinates}
                      setCoordinates={setCoordinates}
                      initialAddress={address}
                      onAddressChange={handleAddressChange}
                      editmerchantId={editmerchantId}
                      validationErrors={errors}
                      brandStars={brandStars}
                    />
                  </SimpleGrid>
                </Box>
                {/* Pricing Section */}
                <Box mb={8}>
                  <Text
                    fontSize="xl"
                    fontWeight="bold"
                    color="gray.800"
                    mb={6}
                    pb={2}
                    borderBottom="2px solid"
                    borderColor="primary"
                  >
                    PRICING
                  </Text>
                  <SimpleGrid
                    columns={{ base: 1, md: 2, lg: 3 }}
                    spacing="24px"
                  >
                    <Controller
                      name="priceStrategyId"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <CommonReactSelect
                          label="Price Strategy"
                          name="priceStrategyId"
                          type="select"
                          options={priceStrategyOptions}
                          value={value}
                          onChange={onChange}
                          register={register}
                          isRequired={true}
                          error={errors?.priceStrategyId}
                          getOptionLabel={(option) =>
                            `${option?.name || 'Unnamed'}`
                          }
                          getOptionValue={(option) => option?.id}
                          placeholderValue={
                            isPriceStrategyLoading
                              ? 'Loading price strategy'
                              : 'Price Strategy'
                          }
                        />
                      )}
                    />
                  </SimpleGrid>

                  {/* Read-only price strategy details — shown when a strategy is selected */}
                  {watch('priceStrategyId') && (
                    <Box
                      mt={4}
                      p={4}
                      bg="gray.50"
                      borderRadius="md"
                      border="1px solid"
                      borderColor="gray.200"
                    >
                      <Text
                        fontSize="sm"
                        fontWeight="semibold"
                        color="gray.600"
                        mb={3}
                      >
                        Price Strategy Details
                      </Text>
                      <SimpleGrid
                        columns={{ base: 1, md: 2, lg: 4 }}
                        spacing={3}
                      >
                        {[
                          {
                            label: 'Free Duration',
                            value:
                              watch('priceStrategyId')?.freeDuration !== null &&
                              watch('priceStrategyId')?.freeDuration !==
                                undefined
                                ? `${watch('priceStrategyId').freeDuration} min`
                                : 'N/A',
                          },
                          {
                            label: 'Time Interval',
                            value:
                              watch('priceStrategyId')?.priceMinute !== null &&
                              watch('priceStrategyId')?.priceMinute !==
                                undefined
                                ? `${watch('priceStrategyId').priceMinute} min`
                                : 'N/A',
                          },
                          {
                            label: 'Price / Interval',
                            value: watch('priceStrategyId')?.price ?? 'N/A',
                          },
                          {
                            label: 'Deposit',
                            value: watch('priceStrategyId')?.deposit ?? 'N/A',
                          },
                          {
                            label: 'Overtime Fees',
                            value:
                              watch('priceStrategyId')?.overtimeFees ?? 'N/A',
                          },
                          {
                            label: 'Day Capped Amount',
                            value: watch('priceStrategyId')?.maxPrice ?? 'N/A',
                          },
                          {
                            label: 'Days Limit',
                            value: watch('priceStrategyId')?.daysLimit ?? 'N/A',
                          },
                          {
                            label: 'Baije Price ID',
                            value:
                              watch('priceStrategyId')?.baijePriceId ?? 'N/A',
                          },
                        ].map(({ label, value }) => (
                          <Flex
                            key={label}
                            direction="column"
                            gap="2px"
                            padding="6px 8px"
                            borderLeft="2px solid"
                            borderColor="gray.300"
                          >
                            <Text
                              fontSize="xs"
                              color="gray.500"
                              fontWeight="normal"
                            >
                              {label}
                            </Text>
                            <Text
                              fontSize="sm"
                              fontWeight="semibold"
                              color="gray.700"
                            >
                              {value}
                            </Text>
                          </Flex>
                        ))}
                      </SimpleGrid>
                    </Box>
                  )}
                </Box>
                {/* Legal Section */}
                <Box mb={8}>
                  <Text
                    fontSize="xl"
                    fontWeight="bold"
                    color="gray.800"
                    mb={6}
                    pb={2}
                    borderBottom="2px solid"
                    borderColor="primary"
                  >
                    LEGAL
                  </Text>
                  <SimpleGrid
                    columns={{ base: 1, md: 2, lg: 3 }}
                    spacing="24px"
                  >
                    {/* Legal Name */}
                    <CommonInput
                      type="text"
                      name="legalName"
                      labelText="Legal Name"
                      textColor={textColor}
                      brandStars={brandStars}
                      placeholder="Enter Legal Name"
                      register={register('legalName')}
                      errors={errors?.legalName}
                      isValidate={false}
                    />

                    {/* Bank Account */}
                    <CommonInput
                      type="text"
                      name="bankAccountNumber"
                      labelText="IBAN"
                      textColor={textColor}
                      brandStars={brandStars}
                      placeholder="Bank Account Number"
                      register={register('bankAccountNumber')}
                      errors={errors?.bankAccountNumber}
                      isValidate={false}
                    />

                    {/* Business Number Input */}
                    <CommonInput
                      type="text"
                      name="NIF"
                      labelText={getCurrentRegionId() === 2 ? 'NIF' : 'KvK'}
                      textColor={textColor}
                      brandStars={brandStars}
                      placeholder="Business Number"
                      register={register('NIF')}
                      errors={errors?.NIF}
                      isValidate={false}
                    />

                    {/* VAT Number Input */}
                    <CommonInput
                      type="text"
                      name="vatNumber"
                      labelText="VAT Number"
                      textColor={textColor}
                      brandStars={brandStars}
                      placeholder="VAT Number"
                      register={register('vatNumber')}
                      errors={errors?.vatNumber}
                      isValidate={false}
                    />

                    {/* Legal Address Line 1 */}
                    <CommonInput
                      type="text"
                      name="legalAddressLine1"
                      labelText="Address Line 1"
                      textColor={textColor}
                      brandStars={brandStars}
                      placeholder="Enter Address Line 1"
                      register={register('legalAddressLine1')}
                      errors={errors?.legalAddressLine1}
                      isValidate={false}
                    />

                    {/* Legal Address Line 2 */}
                    <CommonInput
                      type="text"
                      name="legalAddressLine2"
                      labelText="Address Line 2 (Optional)"
                      textColor={textColor}
                      brandStars={brandStars}
                      placeholder="Enter Address Line 2"
                      register={register('legalAddressLine2')}
                      errors={errors?.legalAddressLine2}
                      isValidate={false}
                    />

                    {/* Legal Zip Code */}
                    <CommonInput
                      type="text"
                      name="legalZipCode"
                      labelText="Zip Code"
                      textColor={textColor}
                      brandStars={brandStars}
                      placeholder="Enter Zip Code"
                      register={register('legalZipCode')}
                      errors={errors?.legalZipCode}
                      isValidate={false}
                      onBlur={(e) =>
                        setValue(
                          'legalZipCode',
                          formatPostalCode(
                            e.target.value,
                            watch('legalCountry'),
                          ),
                          { shouldValidate: true },
                        )
                      }
                    />

                    {/* Legal Country */}
                    <Controller
                      name="legalCountry"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <CommonReactSelect
                          label="Country"
                          name="legalCountry"
                          type="select"
                          options={countryOptions}
                          value={
                            countryOptions.find((c) => c.name === value) || null
                          }
                          onChange={(option) => {
                            onChange(option?.name || '');
                            setLegalCountryIso(option?.isoCode || '');
                            // Reset dependent fields when the country changes
                            setLegalProvinceIso('');
                            setValue('legalProvince', '');
                            setValue('legalCity', '');
                          }}
                          register={register}
                          isRequired={false}
                          error={errors?.legalCountry}
                          getOptionLabel={(e) => e?.name}
                          getOptionValue={(e) => e?.isoCode}
                        />
                      )}
                    />

                    {/* Legal Province */}
                    <Controller
                      name="legalProvince"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <CommonReactSelect
                          label="Province (Optional)"
                          name="legalProvince"
                          type="select"
                          options={provinceOptions}
                          value={
                            provinceOptions.find((s) => s.name === value) ||
                            null
                          }
                          onChange={(option) => {
                            onChange(option?.name || '');
                            setLegalProvinceIso(option?.isoCode || '');
                            // Reset city when the province changes
                            setValue('legalCity', '');
                          }}
                          register={register}
                          isRequired={false}
                          error={errors?.legalProvince}
                          getOptionLabel={(e) => e?.name}
                          getOptionValue={(e) => e?.isoCode}
                          isDisabled={!legalCountryIso}
                        />
                      )}
                    />

                    {/* Legal City */}
                    <Controller
                      name="legalCity"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <CommonReactSelect
                          label="City (Optional)"
                          name="legalCity"
                          type="select"
                          options={cityOptions}
                          value={
                            cityOptions.find((c) => c.name === value) || null
                          }
                          onChange={(option) => onChange(option?.name || '')}
                          register={register}
                          isRequired={false}
                          error={errors?.legalCity}
                          getOptionLabel={(e) => e?.name}
                          getOptionValue={(e) => e?.name}
                          isDisabled={!legalProvinceIso}
                        />
                      )}
                    />
                  </SimpleGrid>
                </Box>
                {/* Commission Section */}
                <Box mb={8}>
                  <Text
                    fontSize="xl"
                    fontWeight="bold"
                    color="gray.800"
                    mb={6}
                    pb={2}
                    borderBottom="2px solid"
                    borderColor="primary"
                  >
                    COMMISSION
                  </Text>

                  {/* Commission Percentage */}
                  <Box mb={6}>
                    <SimpleGrid
                      columns={{ base: 1, md: 2, lg: 3 }}
                      spacing="24px"
                    >
                      <CommonInput
                        type="number"
                        name="commission"
                        labelText="Commission Rate"
                        textColor={textColor}
                        brandStars={brandStars}
                        placeholder="Commission"
                        register={register('commission')}
                        errors={errors?.commission}
                        isValidate={false}
                        isAddPercentage={true}
                      />
                    </SimpleGrid>
                  </Box>

                  {/* Commission Deductions */}
                  <Box>
                    <Flex align="center" gap={2} mb={4}>
                      <Text
                        fontSize="md"
                        fontWeight="semibold"
                        color="gray.700"
                      >
                        Deduction by order options
                      </Text>
                      <Switch
                        size="md"
                        spacing="20px"
                        colorScheme="gray"
                        isChecked={isDeductionExpanded}
                        onChange={() =>
                          setIsDeductionExpanded(!isDeductionExpanded)
                        }
                      />
                    </Flex>
                    <Collapse in={isDeductionExpanded}>
                      <SimpleGrid
                        columns={{ base: 1, md: 2, lg: 3 }}
                        spacing="20px"
                        bg="gray.50"
                        p={6}
                        borderRadius="12px"
                        border="1px solid"
                        borderColor="gray.200"
                      >
                        <Box>
                          <CommonSwitchInput
                            labelText="Deduct costs of powerbanks lost"
                            name="includePowerbankCost"
                            register={register('includePowerbankCost')}
                            errors={errors?.includePowerbankCost}
                            isValidate={false}
                            textColor="gray.700"
                            brandStars="red.500"
                            control={control}
                            infoText="When a power bank is lost and no payment could be charged for it, its cost is subtracted from the net revenue before commission is calculated, which lowers the merchant's commission. When off, the platform absorbs the loss."
                          />
                        </Box>

                        <Box>
                          <CommonSwitchInput
                            labelText="Deduct costs of purchased powerbanks"
                            name="includeCustomerPurchases"
                            register={register('includeCustomerPurchases')}
                            errors={errors?.includeCustomerPurchases}
                            isValidate={false}
                            textColor="gray.700"
                            brandStars="red.500"
                            control={control}
                            infoText="When a power bank is not returned but the customer was charged (deposit and overtime fees), the resulting gain or shortfall versus the power bank's cost is applied to the net revenue before commission. When off, it does not affect the commission."
                          />
                        </Box>

                        <Box>
                          <CommonSwitchInput
                            labelText="Deduct Transaction Costs?"
                            name="includeTransactionCost"
                            register={register('includeTransactionCost')}
                            errors={errors?.includeTransactionCost}
                            isValidate={false}
                            textColor="gray.700"
                            brandStars="red.500"
                            control={control}
                            infoText="The payment processing fees charged by the payment provider are subtracted from the net revenue before commission is calculated. When off, the platform covers these fees."
                          />
                        </Box>

                        <Box>
                          <CommonSwitchInput
                            labelText="Deduct E-invoice fee"
                            name="includeEInvoiceFee"
                            register={register('includeEInvoiceFee')}
                            errors={errors?.includeEInvoiceFee}
                            isValidate={false}
                            textColor="gray.700"
                            brandStars="red.500"
                            control={control}
                            infoText="The fee for issuing official electronic invoices is subtracted from the net revenue before commission is calculated. When off, the platform covers this fee."
                          />
                        </Box>
                      </SimpleGrid>
                    </Collapse>
                  </Box>
                </Box>
                {/* Free Usage Mode Section - Only for Admin/Dealer */}
                {[sideBarRoutes?.Admin, sideBarRoutes?.Dealer].includes(
                  adminDetails?.role,
                ) && (
                  <Box mb={8}>
                    <Text
                      fontSize="xl"
                      fontWeight="bold"
                      color="gray.800"
                      mb={6}
                      pb={2}
                      borderBottom="2px solid"
                      borderColor="primary"
                    >
                      SETTINGS
                    </Text>
                    <SimpleGrid
                      columns={{ base: 1, md: 2, lg: 3 }}
                      spacing="24px"
                    >
                      <CommonSwitchInput
                        labelText="Free Usage Mode"
                        name="freeUsageMode"
                        register={register('freeUsageMode')}
                        isValidate={false}
                        defaultChecked={false}
                        textColor="gray.700"
                        brandStars="red.500"
                        control={control}
                      />
                    </SimpleGrid>
                  </Box>
                )}
                {/* Operating Hours Section */}
                <Box mb={8}>
                  <Flex
                    align="center"
                    mb={6}
                    pb={2}
                    borderBottom="2px solid"
                    borderColor="primary"
                  >
                    <Text
                      fontSize="xl"
                      fontWeight="bold"
                      color="gray.800"
                      mr={2}
                    >
                      OPERATING HOURS
                    </Text>
                    <Tooltip
                      label="Time entries support UTC timezone. Set your operating hours by toggling each day on/off and specifying opening and closing times."
                      placement="top"
                      hasArrow
                      bg="gray.800"
                      color="white"
                      fontSize="sm"
                      borderRadius="md"
                      py={2}
                      px={3}
                      maxWidth="280px"
                      boxShadow="xl"
                      arrowShadowColor="gray.800"
                    >
                      <Box
                        as="span"
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                        cursor="help"
                        color="gray.600"
                        _hover={{ color: 'gray.800' }}
                      >
                        <VscQuestion size={20} />
                      </Box>
                    </Tooltip>
                  </Flex>

                  <SimpleGrid overflow={'auto'}>
                    <OperatingHours
                      ref={operatingHoursRef}
                      formSchedule={formSchedule}
                      errors={errors}
                      handleSwitchChange={handleWeeklySwitchChange}
                      handleTimeChange={handleTimeChange}
                      handleAddPeriod={handleAddPeriod}
                      handleRemovePeriod={handleRemovePeriod}
                    />
                  </SimpleGrid>
                </Box>
                {/* Holiday Hours Section */}
                <Box mb={8}>
                  {/* <Flex align="center" justify="space-between">
                      <Flex align="center">
                        <Text
                          fontSize="xl"
                          fontWeight="bold"
                          color="gray.800"
                          mr={2}
                        >
                          HOLIDAY HOURS
                        </Text>
                        <Tooltip
                          label="Add special hours for holidays or events."
                          placement="top"
                          hasArrow
                        >
                          <Box
                            as="span"
                            cursor="pointer"
                            color="gray.600"
                            _hover={{ color: 'gray.800' }}
                          >
                            <VscQuestion size={20} />
                          </Box>
                        </Tooltip>
                      </Flex>
                      <Button
                        bg="reviveOrange"
                        color="primary"
                        size="sm"
                        borderRadius="12px"
                        _hover={{ bg: 'reviveOrange', opacity: 0.8 }}
                        onClick={handleAddTemporaryClosure}
                      >
                        <Icon
                          as={FaPlus}
                          width="15px"
                          height="15px"
                          marginRight="5px"
                          color="inherit"
                        />
                        Add
                      </Button>
                    </Flex> */}
                  <Flex
                    align="center"
                    justify="space-between"
                    mb={6}
                    pb={2}
                    borderBottom="2px solid"
                    borderColor="primary"
                  >
                    <Text
                      fontSize="xl"
                      fontWeight="bold"
                      color="gray.800"
                      mr={2}
                    >
                      HOLIDAY HOURS
                    </Text>

                    <Button
                      bg="reviveOrange"
                      color="primary"
                      size="sm"
                      borderRadius="12px"
                      _hover={{ bg: 'reviveOrange', opacity: 0.8 }}
                      onClick={handleAddTemporaryClosure}
                    >
                      <Icon
                        as={FaPlus}
                        width="15px"
                        height="15px"
                        marginRight="5px"
                        color="inherit"
                      />
                      Add
                    </Button>
                  </Flex>
                  <SimpleGrid overflow={'auto'}>
                    <OperatingHours
                      ref={holidayHoursRef}
                      formSchedule={temporaryClosures}
                      handleDateChange={handleTemporaryClosureChange}
                      handleReasonChange={handleTemporaryClosureChange}
                      handleSwitchChange={handleTemporaryClosureChange}
                      handleTimeChange={handleClosureTimeChange}
                      handleAddPeriod={handleAddClosurePeriod}
                      handleRemovePeriod={handleRemoveClosurePeriod}
                      handleRemoveDateSlot={handleRemoveTemporaryClosure}
                      isTemporaryClosure={true}
                      merchantTemporaryHours={
                        viewMerchantDetails?.merchantTemporaryHours
                      }
                    />
                  </SimpleGrid>
                  {/* </Box> */}
                  {/* <VStack spacing={4} align="stretch">
                      {temporaryClosures.map((closure, index) => (
                        <HStack key={index} spacing={4} align="center">
                          <Input
                            type="date"
                            value={closure.date}
                            onChange={(e) =>
                              handleTemporaryClosureChange(
                                index,
                                'date',
                                e.target.value,
                              )
                            }
                            width="150px"
                          />
                          <Switch
                            isChecked={closure.isOpen}
                            colorScheme="teal"
                            onChange={(e) =>
                              handleTemporaryClosureChange(
                                index,
                                'isOpen',
                                e.target.checked,
                              )
                            }
                            sx={{
                              'span.chakra-switch__track[data-checked]': {
                                backgroundColor: '#203946',
                              },
                            }}
                          />
                          {closure.isOpen ? (
                            <VStack spacing={2} align="stretch">
                              {closure.periods.map((period, periodIndex) => (
                                <HStack key={periodIndex} spacing={2}>
                                  <Input
                                    type="time"
                                    value={period.openingTime}
                                    onChange={(e) =>
                                      handleClosureTimeChange(
                                        index,
                                        periodIndex,
                                        'openingTime',
                                        e.target.value,
                                      )
                                    }
                                    width="120px"
                                  />
                                  <Text>to</Text>
                                  <Input
                                    type="time"
                                    value={period.closingTime}
                                    onChange={(e) =>
                                      handleClosureTimeChange(
                                        index,
                                        periodIndex,
                                        'closingTime',
                                        e.target.value,
                                      )
                                    }
                                    width="120px"
                                  />
                                  {periodIndex === closure.periods.length - 1 &&
                                    closure.periods.length < 3 && (
                                      <IconButton
                                        icon={<AddIcon />}
                                        size="sm"
                                        onClick={() =>
                                          handleAddClosurePeriod(index)
                                        }
                                      />
                                    )}
                                  {closure.periods.length > 1 && (
                                    <IconButton
                                      icon={<DeleteIcon />}
                                      size="sm"
                                      colorScheme="red"
                                      variant="ghost"
                                      onClick={() =>
                                        handleRemoveClosurePeriod(
                                          index,
                                          periodIndex,
                                        )
                                      }
                                    />
                                  )}
                                </HStack>
                              ))}
                            </VStack>
                          ) : (
                            <Text color="gray.500" flex="1">
                              Closed
                            </Text>
                          )}
                          <Input
                            placeholder="Reason"
                            value={closure.reason}
                            onChange={(e) =>
                              handleTemporaryClosureChange(
                                index,
                                'reason',
                                e.target.value,
                              )
                            }
                            width="200px"
                          />
                        </HStack>
                      ))}
                    </VStack> */}
                  <FormLabel flex={'none'} margin={'0px'} color={textColor}>
                    Stations
                  </FormLabel>
                  {stationIds?.map((stationId) => (
                    <Flex key={stationId} gap={1} align="center">
                      <Input
                        value={stationId}
                        readOnly
                        variant="auth"
                        fontSize="sm"
                        mb="2px"
                        fontWeight="500"
                        size="lg"
                        sx={{
                          backgroundColor: editmerchantId ? '#f5f5f5' : 'white',
                          color: editmerchantId ? '#888' : '#000',
                          border: editmerchantId
                            ? '1px solid #ddd'
                            : '1px solid #ccc',
                          cursor: editmerchantId ? 'not-allowed' : 'text',
                        }}
                      />
                      <IconButton
                        aria-label="Remove Station"
                        icon={<CloseIcon />}
                        size="sm"
                        onClick={() => handleRemoveStationId(stationId)}
                      />
                    </Flex>
                  ))}
                </Box>
                {/* )} */}
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing="24px">
                  <Button
                    type="button"
                    bg="reviveOrange"
                    mt="50px"
                    h="50"
                    color="primary"
                    borderRadius="12px"
                    _hover={{ bg: 'reviveOrange', opacity: 0.8 }}
                    onClick={onOpen}
                  >
                    <Icon
                      as={FaPlus}
                      width="15px"
                      height="15px"
                      borderRadius="12px"
                      marginRight="5px"
                      color="inherit"
                    />
                    Add Station
                  </Button>
                </SimpleGrid>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing="24px">
                  <CommonSubmitBtn BTNtext="Submit" isLoading={isLoading} />
                </SimpleGrid>
                <StationAllocationListModal
                  isOpen={isOpen}
                  onClose={onClose}
                  stationIds={stationIds}
                  setStationIds={setStationIds}
                  temStationIds={temStationIds}
                  setTempStationIds={setTempStationIds}
                />
                {generateLocationModal && (
                  <GenerateLocationConfirmModal
                    showModal={generateLocationModal}
                    setShowModal={setGenerateLocationModal}
                    setTempLocationId={setTempLocationId}
                    setValue={setValue}
                    editmerchantId={editmerchantId}
                  />
                )}
              </form>
            )}
          </Card>
        </Flex>
      </Box>
    </>
  );
};

export default MerchantAddEdit;
